import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie, ArrowLeft, ExternalLink } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const Section = ({ number, title, children }) => (
  <motion.div variants={fadeInUp} className="mb-10 sm:mb-14" data-testid={`cookie-section-${number}`}>
    <div className="flex items-baseline gap-3 sm:gap-4 mb-4 sm:mb-5">
      <span className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl text-[#D4AF37] font-semibold leading-none">
        {number}.
      </span>
      <h2 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl text-white">
        {title}
      </h2>
    </div>
    <div className="pl-0 sm:pl-10 text-zinc-400 text-sm sm:text-base leading-relaxed space-y-4">
      {children}
    </div>
  </motion.div>
);

const SubSection = ({ number, title, children }) => (
  <div className="mb-6 sm:mb-8">
    <h3 className="font-['Cormorant_Garamond'] text-lg sm:text-xl text-white/90 mb-3 flex items-baseline gap-2">
      <span className="text-[#D4AF37]/70 text-base">{number}</span>
      {title}
    </h3>
    <div className="text-zinc-400 text-sm sm:text-base leading-relaxed space-y-3 pl-0 sm:pl-6">
      {children}
    </div>
  </div>
);

const BrowserLink = ({ name, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-between p-3 sm:p-4 border border-white/5 hover:border-[#D4AF37]/30 transition-all group"
  >
    <span className="text-white/80 text-sm sm:text-base group-hover:text-[#D4AF37] transition-colors">{name}</span>
    <ExternalLink className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#D4AF37] transition-colors" strokeWidth={1.5} />
  </a>
);

export default function CookiePolicyPage() {
  return (
    <div data-testid="cookie-policy-page">
      {/* Hero */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-16 bg-[#030303] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-10 right-1/4 w-72 sm:w-[500px] h-72 sm:h-[500px] bg-[#D4AF37] rounded-full filter blur-[120px] sm:blur-[200px]" />
        </div>

        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-4xl relative z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={fadeInUp}>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-zinc-500 hover:text-[#D4AF37] transition-colors text-xs sm:text-sm mb-8 sm:mb-12"
                data-testid="cookie-back-link"
              >
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
                Vissza a főoldalra
              </Link>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
              <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-[#D4AF37]" strokeWidth={1.5} />
              <p className="text-[0.6rem] sm:text-[0.65rem] text-[#D4AF37] tracking-[0.2em] sm:tracking-[0.3em] uppercase">
                Jogi dokumentum
              </p>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl text-white mb-4 sm:mb-5 leading-tight"
            >
              Cookie Szabályzat
            </motion.h1>

            <motion.div variants={fadeInUp} className="gold-line mb-5 sm:mb-6" />

            <motion.p variants={fadeInUp} className="text-zinc-500 text-xs sm:text-sm">
              Hatálybalépés dátuma: 2026. április 9.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-zinc-400 text-xs sm:text-sm mt-3 max-w-2xl leading-relaxed">
              Jelen szabályzat a stryxhungary.hu weboldalon (üzemeltető: Prikler Márk) alkalmazott sütikre vonatkozik.
              A weboldal használatával Ön elfogadja a jelen szabályzat rendelkezéseit.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 sm:py-16 bg-[#050505]">
        <div className="container mx-auto px-5 sm:px-6 md:px-8 max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            variants={staggerContainer}
          >
            {/* 1. Mik azok a sütik */}
            <Section number="1" title="Mik azok a sütik (cookie-k)?">
              <p>
                A cookie-k kisméretű adatfájlok, amelyeket a weboldal küld a böngészőnek. Segítik a weboldal
                működését, javítják a felhasználói élményt, és lehetővé teszik a felhasználói viselkedés elemzését,
                valamint a releváns hirdetések megjelenítését.
              </p>
            </Section>

            {/* 2. Hogyan működik */}
            <Section number="2" title="Hogyan működik a cookie-k használata?">
              <p>
                A rendszer egy egyedi azonosítót hoz létre, amelyet a böngésző eltárol. Visszatéréskor a böngésző
                elküldi ezt az azonosítót, így működik például:
              </p>
              <ul className="list-none space-y-2 text-xs sm:text-sm">
                {[
                  "Bejelentkezési állapot megjegyzése",
                  "Űrlapértékek tárolása",
                  "Konverziók mérése",
                  "Remarketing-hirdetések",
                  "Személyre szabott tartalom"
                ].map((item, idx) => (
                  <li key={idx} className="flex gap-2 items-start">
                    <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                    {item}
                  </li>
                ))}
              </ul>
            </Section>

            {/* 3. A weboldalon használt sütik céljai */}
            <Section number="3" title="A weboldalon használt sütik céljai">
              <p>
                A weboldal sütiket használ az alábbi célokra:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {[
                  "Weboldal működéséhez",
                  "Felhasználói élmény javítása",
                  "Statisztikai mérések",
                  "Teljesítménymérés",
                  "Hirdetések",
                  "Kampánykövetés",
                  "CRM-folyamatok"
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center border border-white/5 p-3 text-xs sm:text-sm">
                    <span className="text-[#D4AF37] text-[6px]">&#9679;</span>
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* 4. A sütik típusai */}
            <Section number="4" title="A sütik típusai">
              <SubSection number="4.1" title="Alapműködést biztosító sütik">
                <p>
                  Session sütik, értesítési üzenetek állapota, bejelentkezési adatok, sütibeállítások, anonimizált
                  Analytics sütik.
                </p>
              </SubSection>

              <SubSection number="4.2" title="Statisztikai és teljesítménymérő sütik">
                <p>
                  Google Analytics, Google Optimize – a felhasználói viselkedést elemzik, személyes azonosítás nélkül.
                </p>
              </SubSection>

              <SubSection number="4.3" title="Célzó- és hirdetési sütik">
                <div className="space-y-3 mt-2">
                  {[
                    { name: "Google Ads (Google Adwords)", url: "https://www.google.com/intl/hu/policies/privacy" },
                    { name: "Facebook (Meta)", url: "https://www.facebook.com/help/cookies/" },
                    { name: "Instagram", url: "https://help.instagram.com/402411646841720" },
                    { name: "TikTok Pixel", url: "https://www.tiktok.com/legal/page/us/privacy-policy/en" },
                    { name: "Google Analytics", url: "https://www.google.com/intl/hu/policies/privacy" },
                    { name: "Google Tag Manager", url: "https://www.google.com/intl/hu/policies/privacy" },
                    { name: "ActiveCampaign", url: "https://www.activecampaign.com/legal/privacy-policy" },
                    { name: "Microsoft Clarity", url: "https://clarity.microsoft.com/privacy" },
                    { name: "GoHighLevel", url: "https://www.gohighlevel.com/privacy-policy" },
                  ].map((service, idx) => (
                    <a
                      key={idx}
                      href={service.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 border border-white/5 hover:border-[#D4AF37]/20 transition-all group"
                    >
                      <span className="text-white/70 text-xs sm:text-sm group-hover:text-[#D4AF37] transition-colors">{service.name}</span>
                      <ExternalLink className="w-3 h-3 text-zinc-700 group-hover:text-[#D4AF37] transition-colors flex-shrink-0" strokeWidth={1.5} />
                    </a>
                  ))}
                </div>
              </SubSection>

              <SubSection number="4.4" title="GoHighLevel sütik">
                <p>
                  Kampány- és funnelkövetés, űrlapadatok, automatizált e-mail/SMS indítás.
                </p>
              </SubSection>
            </Section>

            {/* 5. A sütik kezelése és kikapcsolása */}
            <Section number="5" title="A sütik kezelése és kikapcsolása">
              <p>
                A felhasználó a böngészőjében bármikor módosíthatja vagy törölheti a sütiket.
                A tiltás csökkentheti a weboldal működését.
              </p>
            </Section>

            {/* 6. Böngésző beállítások */}
            <Section number="6" title="Böngészők süti beállításai">
              <p className="mb-4">A legnépszerűbb böngészők süti beállításairól az alábbi linkeken tájékozódhat:</p>
              <div className="space-y-2">
                <BrowserLink name="Google Chrome" url="https://support.google.com/chrome/answer/95647" />
                <BrowserLink name="Mozilla Firefox" url="https://support.mozilla.org/hu/kb/sutik-engedelyezese-es-tiltasa" />
                <BrowserLink name="Microsoft Internet Explorer" url="https://support.microsoft.com/en-us/topic/how-to-delete-cookie-files-in-internet-explorer-7-x-8-x-9-x-10-x-11-x-4976777d-2979-7fa7-3a99-1831f8506f5e" />
                <BrowserLink name="Microsoft Edge" url="https://support.microsoft.com/en-us/microsoft-edge/view-cookies-in-microsoft-edge-a7d95376-f2cd-8e4a-25dc-1de753474879" />
                <BrowserLink name="Safari (Mac)" url="https://support.apple.com/hu-hu/guide/safari/sfri11471/mac" />
              </div>
            </Section>

            {/* 7. Jogszabályi háttér */}
            <Section number="7" title="Jogszabályi háttér">
              <p>A jelen szabályzat az alábbi jogszabályok rendelkezései alapján készült:</p>
              <ul className="list-none space-y-2 text-xs sm:text-sm mt-3">
                {[
                  "2003. évi C. törvény",
                  "2001. évi CVIII. törvény",
                  "2011. évi CXII. törvény",
                  "2002/58/EK irányelv",
                  "GDPR (EU 2016/679 rendelet)"
                ].map((law, idx) => (
                  <li key={idx} className="flex gap-2 items-start">
                    <span className="text-[#D4AF37] mt-1.5 text-[6px]">&#9679;</span>
                    {law}
                  </li>
                ))}
              </ul>
            </Section>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
